import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AdminLayout from '../../../components/feature/AdminLayout';
import {
  fetchRecruitmentApplications,
  fetchRecruitmentSummary,
  type ApplicationStatus,
  type RecruitmentApplication,
  type RecruitmentSummary,
} from '../../../api/recruitment.api';

const PIPELINE: ApplicationStatus[] = [
  'new',
  'screening',
  'shortlisted',
  'interview',
  'final_review',
  'accepted',
  'rejected',
];

function relativeDate(iso?: string | null, todayLabel = 'اليوم'): string {
  if (!iso) return '—';
  const d = new Date(iso);
  const now = new Date();
  const same =
    d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
  if (same) return todayLabel;
  return d.toLocaleDateString('ar-EG');
}

export default function RecruitmentApplicationsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [items, setItems] = useState<RecruitmentApplication[]>([]);
  const [summary, setSummary] = useState<RecruitmentSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    const [list, sums] = await Promise.all([
      fetchRecruitmentApplications({ search, status: status || undefined }),
      fetchRecruitmentSummary(),
    ]);
    setLoading(false);
    if (list.ok === false) {
      if (list.unauthorized) {
        navigate('/admin/login', { replace: true });
        return;
      }
      setError(list.message);
      return;
    }
    setItems(list.data);
    if (sums.ok) setSummary(sums.data);
  }, [navigate, search, status]);

  useEffect(() => {
    void load();
  }, [load]);

  const cards: { key: keyof RecruitmentSummary; label: string; color: string }[] = [
    { key: 'total', label: t('recruitment.summary.total'), color: 'text-gray-900' },
    { key: 'new', label: t('recruitment.app_status.new'), color: 'text-sky-700' },
    { key: 'screening', label: t('recruitment.app_status.screening'), color: 'text-indigo-700' },
    { key: 'interview', label: t('recruitment.app_status.interview'), color: 'text-amber-700' },
    { key: 'accepted', label: t('recruitment.app_status.accepted'), color: 'text-emerald-700' },
    { key: 'rejected', label: t('recruitment.app_status.rejected'), color: 'text-rose-600' },
  ];

  return (
    <AdminLayout>
      <div className="flex flex-col gap-5">
        <div>
          <h1 className="text-xl font-black text-gray-900">{t('page.recruitment_applications.title')}</h1>
          <p className="text-sm text-gray-500 mt-0.5">{t('page.recruitment_applications.subtitle')}</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
          {cards.map((c) => (
            <button
              key={c.key}
              type="button"
              onClick={() => setStatus(c.key === 'total' ? '' : String(c.key))}
              className="bg-white rounded-xl p-4 ring-1 ring-gray-100 text-start"
            >
              <p className="text-xs text-gray-400 font-medium">{c.label}</p>
              <p className={`text-2xl font-black mt-1 ${c.color}`}>{loading || !summary ? '…' : summary[c.key]}</p>
            </button>
          ))}
        </div>

        <div className="rounded-2xl bg-white ring-1 ring-gray-100 overflow-hidden">
          <div className="p-3 border-b border-gray-100 flex flex-col sm:flex-row gap-2">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void load();
              }}
              placeholder={t('recruitment.applications.search')}
              className="flex-1 rounded-xl border border-gray-200 px-3 py-2 text-sm"
            />
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="rounded-xl border border-gray-200 px-3 py-2 text-xs font-bold bg-white"
            >
              <option value="">{t('recruitment.filter.all')}</option>
              {PIPELINE.map((s) => (
                <option key={s} value={s}>
                  {t(`recruitment.app_status.${s}`)}
                </option>
              ))}
            </select>
            <button type="button" onClick={() => void load()} className="px-3 py-2 rounded-xl bg-gray-900 text-white text-xs font-bold">
              {t('recruitment.refresh')}
            </button>
          </div>

          {error ? <div className="m-3 rounded-xl bg-rose-50 text-rose-700 text-sm px-3 py-2">{error}</div> : null}

          <div className="overflow-x-auto">
            <table className="w-full text-xs min-w-[720px]">
              <thead className="bg-gray-50 text-gray-500">
                <tr>
                  {[
                    t('recruitment.applications.col.candidate'),
                    t('recruitment.applications.col.job'),
                    t('recruitment.applications.col.experience'),
                    t('recruitment.applications.col.date'),
                    t('recruitment.applications.col.status'),
                  ].map((h) => (
                    <th key={h} className="text-right font-bold px-3 py-3 whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={5} className="px-3 py-10 text-center text-gray-400">
                      {t('recruitment.loading')}
                    </td>
                  </tr>
                ) : items.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-3 py-10 text-center text-gray-400">
                      {t('recruitment.applications.empty')}
                    </td>
                  </tr>
                ) : (
                  items.map((row) => (
                    <tr
                      key={row.id}
                      onClick={() => navigate(`/recruitment/applications/${row.id}`)}
                      className="border-t border-gray-50 cursor-pointer hover:bg-teal-50/40"
                    >
                      <td className="px-3 py-3">
                        <p className="font-bold text-gray-900">{row.full_name}</p>
                        <p className="text-[11px] text-gray-400" dir="ltr">
                          {row.application_number}
                        </p>
                      </td>
                      <td className="px-3 py-3">{row.job?.title ?? t('recruitment.applications.general')}</td>
                      <td className="px-3 py-3">
                        {row.years_experience != null
                          ? t('recruitment.applications.years', { count: row.years_experience })
                          : '—'}
                      </td>
                      <td className="px-3 py-3 text-gray-500">{relativeDate(row.created_at, t('recruitment.today'))}</td>
                      <td className="px-3 py-3 font-bold">{t(`recruitment.app_status.${row.status}`)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
